'use client';

import { ImageSquare } from '@phosphor-icons/react';

const GALLERY_ROW1 = [
  { id: 1, src: 'https://cdn.aziatochka.ru/aziatochka/01.webp', alt: 'Фото 1' },
  { id: 2, src: 'https://cdn.aziatochka.ru/aziatochka/02.webp', alt: 'Фото 2' },
  { id: 3, src: 'https://cdn.aziatochka.ru/aziatochka/03.webp', alt: 'Фото 3' },
  { id: 4, src: 'https://cdn.aziatochka.ru/aziatochka/04.webp', alt: 'Фото 4' },
  { id: 5, src: 'https://cdn.aziatochka.ru/aziatochka/05.webp', alt: 'Фото 5' },
  { id: 6, src: 'https://cdn.aziatochka.ru/aziatochka/06.webp', alt: 'Фото 6' },
];
const GALLERY_ROW2 = [
  { id: 7, src: 'https://cdn.aziatochka.ru/aziatochka/07.webp', alt: 'Фото 7' },
  { id: 8, src: 'https://cdn.aziatochka.ru/aziatochka/08.webp', alt: 'Фото 8' },
  { id: 9, src: 'https://cdn.aziatochka.ru/aziatochka/09.webp', alt: 'Фото 9' },
  { id: 10, src: 'https://cdn.aziatochka.ru/aziatochka/10.webp', alt: 'Фото 10' },
  { id: 11, src: 'https://cdn.aziatochka.ru/aziatochka/11.webp', alt: 'Фото 11' },
  { id: 12, src: 'https://cdn.aziatochka.ru/aziatochka/12.webp', alt: 'Фото 12' },
];

const MISSIONS = [
  {
    title: 'Накормим, когда устали',
    text: 'Мы накормим, когда вы устали или просто проголодались. С нами можно разделить любой момент.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/04.webp',
  },
  {
    title: 'Доставим к празднику',
    text: 'Привезём сет к праздничному столу — на день рождения, корпоратив или просто так.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/05.webp',
  },
  {
    title: 'Скажем за вас',
    text: 'Привезём роллы, которые скажут о важном за вас — лучше любых слов.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/06.webp',
  },
  {
    title: 'Поднимем настроение',
    text: 'Отправим прикольный мемчик, когда вам грустно — мы всегда на связи.',
    photo: 'https://cdn.aziatochka.ru/aziatochka/07.webp',
  },
  {
    title: 'Выручим до зарплаты',
    text: 'Дадим промокод, если до зарплаты ещё неделя. На нас можно положиться!',
    photo: 'https://cdn.aziatochka.ru/aziatochka/08.webp',
  },
];

function GalleryImage({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative shrink-0 w-[260px] sm:w-[320px] lg:w-[380px] h-[180px] sm:h-[220px] lg:h-[260px] rounded-2xl overflow-hidden group">
      {src ? (
        <img src={src} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[var(--surface-tertiary)]">
          <ImageSquare weight="thin" className="w-8 h-8 text-[var(--text-tertiary)] mb-1" />
          <span className="text-[10px] text-[var(--text-tertiary)]">Фото</span>
        </div>
      )}
    </div>
  );
}

function InfiniteRow({ items, direction }: { items: typeof GALLERY_ROW1; direction: 'left' | 'right' }) {
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden">
      <div className="flex gap-4 w-max" style={{ animation: `about-marquee-${direction} ${items.length * 6}s linear infinite` }}>
        {doubled.map((img, i) => (
          <GalleryImage key={`${img.id}-${i}`} src={img.src} alt={img.alt} />
        ))}
      </div>
    </div>
  );
}

/* Card with photo + text overlay at bottom */
function MissionCard({ m, i, className }: { m: typeof MISSIONS[number]; i: number; className?: string }) {
  return (
    <div
      className={`group animate-fade-up ${className || ''}`}
      style={{ animationDelay: `${i * 80}ms` }}
    >
      <div className="relative rounded-3xl overflow-hidden bg-[var(--surface-primary)] border border-[var(--border-default)] h-full transition-all duration-500 hover:shadow-2xl hover:shadow-[var(--primary)]/8 hover:-translate-y-1 hover:border-[var(--border-hover)]">
        {/* Photo fills the card */}
        <div className="absolute inset-0">
          <img
            src={m.photo}
            alt={m.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
          {/* Dark gradient from bottom for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>

        {/* Number badge */}
        <div className="absolute top-4 left-4 lg:top-5 lg:left-5">
          <span
            className="inline-flex items-center justify-center w-9 h-9 lg:w-10 lg:h-10 rounded-xl text-xs lg:text-sm font-numbers font-bold text-[var(--text-on-primary)] shadow-lg backdrop-blur-sm"
            style={{ background: 'linear-gradient(135deg, rgba(123,66,255,0.9), rgba(91,40,209,0.9))' }}
          >
            {String(i + 1).padStart(2, '0')}
          </span>
        </div>

        {/* Text at bottom */}
        <div className="relative z-10 flex flex-col justify-end h-full p-5 lg:p-7">
          <h3 className="text-lg lg:text-xl font-heading font-black text-white mb-1.5 lg:mb-2">
            {m.title}
          </h3>
          <p className="text-xs lg:text-sm text-white/75 leading-relaxed line-clamp-3">
            {m.text}
          </p>
        </div>
      </div>
    </div>
  );
}

export function AboutMission() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-transparent via-[var(--primary)]/20 to-transparent" />

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 sm:pt-32 pb-10 sm:pb-16">
        <div className="text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tight text-[var(--text-primary)] leading-[0.95] mb-6 animate-fade-up">
            В чём наша <span className="text-[var(--primary)]">миссия</span>
          </h2>
          <p className="text-lg sm:text-xl text-[var(--text-secondary)] leading-relaxed max-w-3xl mx-auto animate-fade-up" style={{ animationDelay: '160ms' }}>
            Создание классного продукта для людей, которым стиль, позитивная энергия,
            дружеский лайфстайл и честная сделка важны так же, как вкус.
          </p>
        </div>
      </div>

      {/* ── Desktop: asymmetric bento grid ── */}
      <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 pb-8">
        {/*
          Layout:
          Row 1: [card-0 wide 3/5] [card-1 narrow 2/5]  — tall
          Row 2: [card-2 narrow 2/5] [card-3 wide 3/5]  — medium
          Row 3: [card-4 full width]                     — short/wide
        */}
        <div className="grid grid-cols-5 gap-5 auto-rows-[280px]">
          {/* Row 1 */}
          <MissionCard m={MISSIONS[0]} i={0} className="col-span-3 row-span-1" />
          <MissionCard m={MISSIONS[1]} i={1} className="col-span-2 row-span-1" />
          {/* Row 2 */}
          <MissionCard m={MISSIONS[2]} i={2} className="col-span-2 row-span-1" />
          <MissionCard m={MISSIONS[3]} i={3} className="col-span-3 row-span-1" />
          {/* Row 3 — wide banner */}
          <MissionCard m={MISSIONS[4]} i={4} className="col-span-5 row-span-1" />
        </div>
      </div>

      {/* ── Mobile: horizontal swipe ── */}
      <div className="lg:hidden about-hide-scrollbar" style={{ overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' as never }}>
        <div className="flex gap-4 px-4 sm:px-6 w-max pb-4">
          {MISSIONS.map((m, i) => (
            <div key={m.title} className="shrink-0 w-[280px] sm:w-[320px] h-[340px] sm:h-[380px]">
              <MissionCard m={m} i={i} className="h-full" />
            </div>
          ))}
          {/* Spacer for last card peek */}
          <div className="shrink-0 w-4" />
        </div>
      </div>

      {/* Marquee gallery */}
      <div className="py-16 sm:py-24 space-y-4 sm:space-y-5">
        <InfiniteRow items={GALLERY_ROW1} direction="left" />
        <InfiniteRow items={GALLERY_ROW2} direction="right" />
      </div>
    </section>
  );
}
